import { useState } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MoneyInput from 'components/Inputs/MoneyInput'
import TextInput from 'components/Inputs/TextInput'
import SelectAsync from 'components/Inputs/SelectAsync'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import bankingTransferValidator from 'validators/bankingTransferValidator'
import { FaExchangeAlt } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Row,
  Form,
  FormField,
  FieldLabel,
  FormActions,
  HighlightRow,
  CardHeader,
  CardTitle,
  Caption,
  DataLabel,
  StatusBadge,
  Divider,
  InfoText,
  WarningText,
  PlainMoneyText
} from '../../styles'

export default function TransferForm ({
  availableBalance,
  requestConfig,
  onCompleted = () => {}
}) {
  const [receipt, setReceipt] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)

  const toggleConfirmDialog = () => setShowConfirmDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  /*
    A API devolve o documento já mascarado — o suficiente para o titular conferir que
    escolheu a conta certa, sem transformar a busca em uma listagem de CPFs.
  */
  const getTargets = async query => {
    if (query.length < BankingService.TARGET_QUERY_MIN_LENGTH) return []

    try {
      const { data: targets } = await cutileiApi.get (
        `/banking_account/transfer_targets?query=${encodeURIComponent (query)}`, requestConfig
      )

      return targets.map (target => ({
        label: BankingService.getTargetLabel (target),
        value: target
      }))
    } catch (error) {
      console.log (error.response ?? error)
      return []
    }
  }

  return (
    <Formik
      validationSchema={bankingTransferValidator}
      /*
        O saldo é recarregado após cada transferência: sem reinicializar, o teto da
        validação continuaria sendo o saldo de antes da operação.
      */
      enableReinitialize
      initialValues={{
        availableBalance,
        target: null,
        value: 0,
        description: ''
      }}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting (true)
          setRequestError (null)

          const { data: transaction } = await cutileiApi.post ('/banking_account/transfers', {
            targetAccountId: values.target.value.id,
            value: values.value,
            description: values.description
          }, requestConfig)

          setSubmitting (false)
          setReceipt ({...transaction, targetName: values.target.value.name})
          resetForm ({
            values: {availableBalance, target: null, value: 0, description: ''}
          })
          toggleOkDialog ()
          onCompleted ()
        } catch (error) {
          setSubmitting (false)
          console.log (error.response ?? error)
          setRequestError (
            error.response?.data?.message ?? 'Não foi possível realizar a transferência'
          )
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        validateForm,
        handleChange,
        handleBlur,
        handleSubmit
      }) => {
        /*
          A confirmação só abre com o formulário válido: o diálogo nomeia o destinatário e
          o valor, e montá-lo a partir de campos inválidos pediria a confirmação de uma
          transferência que a API recusaria em seguida.
        */
        const handleConfirmRequest = async () => {
          const formErrors = await validateForm ()

          if (Object.keys (formErrors).length > 0) return handleSubmit ()

          toggleConfirmDialog ()
        }

        return (
          <Form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Transferir para outra conta Cutilei</CardTitle>
              <FaExchangeAlt size={16} color='#FF9800'/>
            </CardHeader>
            <Caption>
              {'Busque a conta de destino pelo nome, CPF, CNPJ ou e-mail. A transferência '
              + 'entre contas Cutilei é gratuita e cai na hora'}
            </Caption>
            <Divider/>
            <Row>
              <FormField stretch={2}>
                <FieldLabel>Conta de destino</FieldLabel>
                <SelectAsync
                  name='target'
                  placeholder='Conta de destino...'
                  value={values.target}
                  loadOptions={getTargets}
                  error={errors.target}
                  touched={touched.target}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                  isClearable
                  isOptionDisabled={option => !option.value.canReceive}
                  noOptionsMessage='Nenhuma conta encontrada'
                />
                {touched.target && errors.target && (
                  <WarningText>{errors.target}</WarningText>
                )}
              </FormField>
              <FormField>
                <FieldLabel>Valor</FieldLabel>
                <MoneyInput
                  name='value'
                  placeholder='R$ 0,00'
                  value={values.value}
                  maxValue={availableBalance}
                  onValueChange={setFieldValue}
                  onBlur={handleBlur ('value')}
                />
                {touched.value && errors.value && (
                  <WarningText>{errors.value}</WarningText>
                )}
              </FormField>
            </Row>
            <Row>
              <FormField>
                <FieldLabel>Descrição (opcional)</FieldLabel>
                <TextInput
                  name='description'
                  placeholder='Aparece no extrato das duas contas'
                  value={values.description}
                  onChange={handleChange ('description')}
                  onBlur={handleBlur ('description')}
                />
                {touched.description && errors.description && (
                  <WarningText>{errors.description}</WarningText>
                )}
              </FormField>
            </Row>
            {requestError && <WarningText>{requestError}</WarningText>}
            <HighlightRow>
              <DataLabel>
                {values.target
                  ? `Você vai transferir para ${values.target.value.name}`
                  : 'Você vai transferir'}
              </DataLabel>
              <PlainMoneyText value={Number (values.value)}/>
            </HighlightRow>
            <FormActions>
              <MainButton
                label='Transferir'
                onClick={handleConfirmRequest}
                loading={isSubmitting}
                width={150}
              />
            </FormActions>
            {receipt && (
              <>
                <Divider/>
                <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <InfoText>{`Última transferência para ${receipt.targetName}`}</InfoText>
                  <StatusBadge color={BankingService.getTransactionStatusColor (receipt.status)}>
                    {BankingService.getTransactionStatusLabel (receipt.status)}
                  </StatusBadge>
                </Row>
              </>
            )}
            <AlertDialog
              visible={showConfirmDialog}
              title='Confirmar transferência'
              message={
                `Transferir ${Number (values.value).toLocaleString ('pt-BR', {
                  style: 'currency', currency: 'BRL'
                })} para ${values.target?.value.name}?`
              }
              confirmText='Transferir'
              cancelText='Cancelar'
              onConfirm={handleSubmit}
              onClose={toggleConfirmDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title='Transferência realizada'
              message='O valor já está disponível na conta de destino'
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
